// core/auth/guards/role-guard.js (RDARR)
import { useEffect, useMemo } from 'preact/hooks';
import { html } from '@shared/utils/rendering/html';
import { useAuth } from '../context/authentication-context';

export default function withRoleGuard(Wrapped, options = {}) {
    const { required = [], mode = 'any', controls = [], fallback: FallbackComponent } = options;

    const Guard = (props) => {
        const ctx = useAuth();
        if (!ctx) {
            return html`<div class="error-page">Auth wiring error: missing AuthProvider.</div>`;
        }

        const { ready, isAuthenticated, user, roles, login, ensureSession } = ctx;

        const isAllowed = useMemo(() => {
            if (typeof required === 'function') return required(user || null);

            const needed = Array.isArray(required) ? required : [required];

            if (!needed.length) return true;
            const have = new Set(roles);

            return controls.every((c) => c === true) && mode === 'all'
                ? needed.every((r) => have.has(r))
                : needed.some((r) => have.has(r));
        }, [required, roles, mode, user, controls]);

        useEffect(() => {
            (async () => {
                if (!ready) return;
                if (isAuthenticated) return;
                const ok = await ensureSession();
                if (!ok) {
                    const returnUrl = location.pathname + location.search + location.hash;
                    login(returnUrl);
                }
            })();
        }, [ready, isAuthenticated, ensureSession, login]);

        if (!ready) {
            return html`<div class="authenticating p-4"><h2>Authenticating…</h2></div>`;
        }
        if (!isAuthenticated) {
            return html`<div class="redirecting p-4"><h2>Redirecting…</h2></div>`;
        }
        if (!isAllowed) {
            if (FallbackComponent)
                return html`<${FallbackComponent}
                    props=${{ isAuthenticated, isAllowed, required }}
                />`;
            window.dispatchEvent(new CustomEvent('api:forbidden-error'));
            return html`<div class="access-denied p-4">
                <h2 class="mb-2">403 — Access Denied</h2>
                <p>You do not have permission to access this resource.</p>
            </div>`;
        }

        return html`<${Wrapped} ...${props} />`;
    };

    Guard.displayName = `WithRoleGuard(${Wrapped.displayName || Wrapped.name || 'Component'})`;
    return Guard;
}
